import { HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable, LOCALE_ID, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';

import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatomoConsentComponent } from '../common/matomo-consent/matomo-consent.component';

import { environment } from 'src/environments/environment';

import { AuthService } from './auth.service';
import { LocalStorageService } from './local-storage.service';
import { MessageService } from './message.service';

import { ExitDialogComponent } from '../components/exit-dialog/exit-dialog.component';
import { ComponentItem } from '../types/component';
import { FilterConfig, FilterTypes } from '../types/filter-config';

/**
 * service, which shares methods to be used in various components
 */
@Injectable( {
    providedIn: 'root'
} )
export class SharedService {
    /** ignore in coverage */
    #dialog = inject( MatDialog );
    #authService = inject( AuthService );
    #cookieService = inject( CookieService );
    #localStorageService = inject( LocalStorageService );
    #messageService = inject( MessageService );
    #router = inject( Router );

    consentChecked = signal( this.#cookieService.check( 'scope.consent' ) );

    constructor(
        private dialog: MatDialog,
        @Inject( LOCALE_ID ) public locale: string
    ) { }

    /**
     * check if refreshToken is still valid
     * @returns Promise - return refreshToken when valid, otherwise reject
     */
    async checkTokenValidity() {
        let stillValid = true;

        // find refreshToken in localstorage
        const tokenIssuerHost = environment.azure.authorityDomain;
        const item = `${tokenIssuerHost}-refreshtoken`;
        const refreshTokenKey = Object.keys( localStorage ).filter( ( key ) => key.indexOf( item ) > 0 )[0];
        const refreshTokenObj = localStorage.getItem( refreshTokenKey );
        const refreshtoken = JSON.parse( refreshTokenObj! ).secret;

        // check if refreshToken is still valid
        const now = new Date().valueOf();
        const refreshExpireOn = JSON.parse( refreshTokenObj! ).expiresOn * 1000; // convert to milliseconds
        const timeOffset = 1000 * 60 * 60 * 10; // time offset before invalidation
        stillValid = now <= refreshExpireOn - timeOffset;

        return stillValid ? Promise.resolve( refreshtoken ) : Promise.reject();
    }

    // set login cookie to enable re-login after logout and redirect from MS Azure
    prepareSessionRenewal() {
        this.#cookieService.set( 'login', 'true', undefined, '/' );
        this.#authService.logout();
    }

    /**
     * helper function to start Scope Client with required parameters
     *
     * when scope instance version is greater than 24.6.26 refreshtoken is read
     * from localStorage, otherwise accessToken from {@link AuthService} is used.
     *
     * when refreshToken is used, then the expire timestamp is checked
     * if token will expire in less than offset time, the user will be
     * automatically logged out and in again to renew the session.
     *
     * if Scope client is installed, the client will start with that URL.
     * @param {string} version - version of scope server, defaults to 1.0.0
     * @param {string} url - url to scope server
     * @returns {boolean} Promise as boolean
     */
    async launchScope( version = '1.0.0', url: string ): Promise<boolean> {
        return this.checkTokenValidity()
            .then( async ( validToken ) => {
                // always trigger token renewal to prevent expired session
                const token = await this.#authService.retrieveAccessToken();

                let params;
                const useRefreshToken = this.checkVersion( version, '24.6.26' ) >= 0;
                if ( useRefreshToken ) {
                    const tokenIssuerHost = environment.azure.authorityDomain;
                    params = { tokenIssuerHost, refreshtoken: validToken };
                } else {
                    params = { accesstoken: token };
                }

                const account = this.#localStorageService.load( 'account' );
                if ( account ) {
                    const dto = {
                        ...params,
                        preferredLanguage: account.preferredLanguage,
                        formatLocale: account.formatLocale
                    };

                    const query = new URLSearchParams( dto );
                    window.location.href = `${url}?${query}`;

                }
                return true;
            } )
            .catch( () => {
                this.prepareSessionRenewal();
                return false;
            } );
    }

    /**
     * helper function to show consent banner for tracking
     * @param {boolean} simple - flag, if a (simple) choice should be shown,
     * default is true, otherwise a  more granular choice is shown
     */
    handleTrackingConsent( simple: boolean ) {
        const checked = this.#cookieService.get( 'scope.consent' ) === 'true';
        const data = { checked, simple };
        const dialogRef = this.#dialog.open( MatomoConsentComponent, {
            data,
            closeOnNavigation: false,
            enterAnimationDuration: '0',
            exitAnimationDuration: '0',
            panelClass: 'riege-dialog'
        } );

        dialogRef.afterClosed().subscribe( ( consent ) => {
            if ( consent !== undefined ) {
                const expireDate = new Date();

                // As of Chrome release M104 (August 2022) cookies can no longer
                // set an expiration date more than 400 days in the future.
                const currentDate = expireDate.getDate();
                expireDate.setDate( currentDate + 400 );

                this.#cookieService.set( 'scope.consent', consent, expireDate );
                this.consentChecked.set( true );
            }
        } );
    }

    /**
     * helper function to compare given version strings
     * - 0 when first version is same as second
     * - 1 when first version is higher than second
     * - -1 when first version is lower than second
     * @param a version a
     * @param b version b
     * @returns {number} value depending on comparison result
     */
    checkVersion( a: string, b: string ): number {
        const x = a.split( '.' ).map( ( e ) => parseInt( e, 10 ) );
        const y = b.split( '.' ).map( ( e ) => parseInt( e, 10 ) );

        for ( const i in x ) {
            y[i] = y[i] || 0;
            if ( x[i] === y[i] ) {
                continue;
            } else if ( x[i] > y[i] ) {
                return 1;
            } else {
                return -1;
            }
        }
        return y.length > x.length ? -1 : 0;
    }

    /** end of session handling */
    handleSessionExpired() {
        /** show error message */
        const line = $localize`:@@error-msg-loggout-line:Your session has expired.`;
        const line2 = $localize`:@@error-msg-loggout-line2:Click on the OK button to renew your session.`;
        const supportMsg = `${line}\n
                    ${line2}`;

        const msg = {
            text: supportMsg,
            action: $localize`:@@error-msg-loggout-action:OK`
        };
        const snackBarRef = this.#messageService.error( msg );

        // trigger logout on dismiss (action clicked)
        snackBarRef.afterDismissed().subscribe( () => {
            this.#authService.logout();
        } );
    }

    /**
     * handle (404) errors occuring in network stack / on server side
     * @param error - http error object
     * @returns boolean - flag, if error status has been handled or not
     */
    handleServerSideError( error: HttpErrorResponse ): boolean {
        let handled = false;

        switch ( error.status ) {
            case 404:
                this.#router.navigate( ['404'] );
                handled = true;
                break;
        }
        return handled;
    }

    /**
     * helper function to show preview in a sidebar panel
     * parameter which includes the preview component class and data to be shown
     * NOTE: after closed action currently not implemented - no use case
     * @param {ComponentItem} component
     */
    handleShowPreview( component: ComponentItem ) {
        const sidepanelConfig = new MatDialogConfig();
        sidepanelConfig.data = component.data;
        sidepanelConfig.panelClass = ['sidepanel', 'sidepanel--fade-in-right'];

        const dialogRef = this.dialog.open( component.type, sidepanelConfig );

        dialogRef.afterClosed().subscribe( ( result ) => {
            console.log( 'Dialog was closed', result );
        } );
    }

    /**
     * helper function to show dialog when exiting unsaved changes
     * @param {ComponentItem} component
     */
    handleExitDialog( component: ComponentItem ) {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.data = component.data;
        dialogConfig.panelClass = ['exit'];

        const dialogRef = this.dialog.open( ExitDialogComponent, dialogConfig );

        dialogRef.afterClosed().subscribe( ( result ) => {
            console.log( 'Dialog was closed', result );
        } );
    }

    filters = signal<FilterTypes | undefined>( undefined );

    /**
     * helper function to show dialog when exiting unsaved changes
     * @param {ComponentItem} component
     */
    handleFilterSidepanel( component: ComponentItem ) {
        const filterConfig = new MatDialogConfig<FilterConfig>();
        filterConfig.data = component.data;
        filterConfig.panelClass = ['sidepanel', 'sidepanel--fade-in-right'];

        const dialogRef = this.dialog.open( component.type, filterConfig );

        dialogRef.afterClosed().subscribe( ( selectedFilters ) =>
            this.filters.set( selectedFilters ) );
    }

    /**
     * format bytes
     * @param bytes (File size in bytes)
     * @param decimals (Decimals point)
     * @returns {string} formatted string of bytes
     */
    formatBytes( bytes: number, decimals: number = 0 ): string {
        if ( bytes === 0 ) {
            return '0 Bytes';
        }
        const k = 1024;
        const dm = decimals <= 0 ? 0 : decimals || 2;
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
        const i = Math.floor( Math.log( bytes ) / Math.log( k ) );
        return parseFloat( ( bytes / Math.pow( k, i ) ).toFixed( dm ) ) + ' ' + sizes[i];
    }
}
